import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/StatusPill";
import { SmsTemplatePicker, QuickTemplateChips } from "@/components/SmsTemplatePicker";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, ChevronDown, MessageSquare, RefreshCw, Send, RotateCcw, AlertTriangle, Volume2, VolumeX } from "lucide-react";

const MUTE_KEY = "htss_sms_chime_muted";

const STATUS_COLOUR = {
  queued: "amber", sent: "blue", delivered: "green", undelivered: "red",
  failed: "red", received: "grey", read: "green",
};

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function StatusChip({ m }) {
  if (m.direction === "inbound") return <Pill colour="grey" dot={false}>received</Pill>;
  return <Pill colour={STATUS_COLOUR[m.status] || "grey"} dot={false}>{m.status || "queued"}</Pill>;
}

function playReplyChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [[880, 0], [1320, 0.14]].forEach(([f, at]) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "sine"; o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + at);
      g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.28);
      o.connect(g).connect(ctx.destination); o.start(ctx.currentTime + at); o.stop(ctx.currentTime + at + 0.3);
    });
    setTimeout(() => ctx.close(), 800);
  } catch { /* audio blocked until the user interacts with the page */ }
}

export default function JobSmsPanel({ legacyId, customerId, customerName, autoOpen = false, defaultOpen = false, jobs = [] }) {
  const { canAny, can } = useAuth();
  const mayView = canAny(["communications.view", "communications.send", "communications.manage"]);
  const maySend = can("communications.send");

  const [open, setOpen] = useState(autoOpen || defaultOpen);
  const [unread, setUnread] = useState(0);
  const lastInboundRef = useRef(undefined);
  const [muted, setMuted] = useState(() => localStorage.getItem(MUTE_KEY) === "1");
  const toggleMute = () => setMuted((m) => { const n = !m; localStorage.setItem(MUTE_KEY, n ? "1" : "0"); toast.success(n ? "Reply sound muted" : "Reply sound on"); return n; });
  const panelRef = useRef(null);
  useEffect(() => {
    if (autoOpen) { setTimeout(() => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 400); }
  }, [autoOpen]);
  const [loading, setLoading] = useState(true);
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [recipient, setRecipient] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [retrying, setRetrying] = useState(null);
  const scrollRef = useRef(null);
  const [unreadConvs, setUnreadConvs] = useState([]);
  // No job: customer-page mode shows every SMS with this customer and sends on a general thread.
  const custMode = !legacyId;
  const [sendJob, setSendJob] = useState("general");
  const [tplKey, setTplKey] = useState(0);
  const bumpTpl = useCallback(() => setTplKey((k) => k + 1), []);

  const load = useCallback(async () => {
    if (!customerId) { setLoading(false); return; }
    try {
      const [c, n] = await Promise.all([
        custMode ? api.get(`/communications/customers/${customerId}/sms`) : api.get(`/communications/jobs/${legacyId}/conversation`, { params: { customer_id: customerId } }),
        api.get(`/communications/customers/${customerId}/numbers`),
      ]);
      setConv(c.data.conversation);
      const msgs = c.data.messages || [];
      const lastIn = [...msgs].reverse().find((m) => m.direction === "inbound");
      if (lastInboundRef.current !== undefined && lastIn && lastIn.id !== lastInboundRef.current && localStorage.getItem(MUTE_KEY) !== "1") playReplyChime();
      lastInboundRef.current = lastIn ? lastIn.id : null;
      setMessages(msgs);
      setUnread(c.data.unread || 0);
      setUnreadConvs(c.data.unread_conversations || (c.data.conversation ? [c.data.conversation.id] : []));
      const nums = n.data.numbers || [];
      setNumbers(nums);
      // Preselect only when unambiguous (exactly one number). Never auto-pick an ambiguous one.
      setRecipient((prev) => prev || (nums.length === 1 ? nums[0].number : (c.data.conversation?.to_number || "")));
    } catch (e) {
      // 403 (no perm) or other — keep panel quiet
    } finally { setLoading(false); }
  }, [legacyId, customerId, custMode]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  // Opening the panel (with new replies showing) marks them read after a short pause.
  useEffect(() => {
    if (!open || !unread || !unreadConvs.length) return undefined;
    const t = setTimeout(() => {
      Promise.all(unreadConvs.map((id) => api.post(`/communications/conversations/${id}/read`))).then(() => setUnread(0)).catch(() => {});
    }, 2500);
    return () => clearTimeout(t);
  }, [open, unread, unreadConvs]);

  // Light polling for incoming replies / delivery updates while the panel is open.
  useEffect(() => {
    if (!open || !customerId) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [open, customerId, load]);

  useEffect(() => {
    if (open && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    if (!body.trim()) { toast.error("Message cannot be empty"); return; }
    if (/\{(first_name|name|job_number|delivery_date|delivery_time|engineer_name)\}/.test(body)) { toast.error("Replace the {…} placeholders before sending"); return; }
    if (!recipient) { toast.error("Select a recipient number"); return; }
    setBusy(true);
    try {
      const client_key = (window.crypto?.randomUUID?.() || String(Date.now() + Math.random()));
      const toJob = custMode && sendJob !== "general" ? sendJob : null;
      const r = await api.post(custMode && !toJob ? `/communications/customers/${customerId}/messages` : `/communications/jobs/${toJob || legacyId}/messages`, {
        customer_id: customerId, to_number: recipient, body, client_key,
      });
      setBody("");
      if (r.data.dispatched) toast.success("SMS sent");
      else toast.message(r.data.note || "Message queued (live sending disabled)");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const retry = async (m) => {
    setRetrying(m.id);
    try {
      const r = await api.post(`/communications/messages/${m.id}/retry`);
      if (r.data.dispatched) toast.success("Message resent");
      else toast.message(r.data.error || r.data.note || "Retry queued (live sending disabled)");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRetrying(null); }
  };

  if (!mayView) return null;

  return (
    <section ref={panelRef} className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="job-sms-panel">
      <div className="flex items-center gap-2 px-5 py-3.5 bg-slate-50/70 border-b border-slate-100">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex-1 flex items-center gap-2 text-left text-base font-bold text-slate-900" data-testid="job-sms-toggle">
          <MessageSquare className="w-4 h-4 text-indigo-500" /> Communications (SMS)
          <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold" data-testid="job-sms-count">{messages.length}</span>
          {unread > 0 && <span className="inline-flex items-center h-5 px-1.5 rounded-full bg-rose-600 text-white text-[11px] font-semibold" data-testid="job-sms-unread">{unread} new</span>}
        </button>
        {open && (
          <button type="button" onClick={toggleMute} className={`${muted ? "text-slate-400" : "text-indigo-500"} hover:text-slate-600`}
            title={muted ? "Reply sound off — click to turn on" : "Reply sound on — click to mute"} aria-pressed={!muted} data-testid="job-sms-chime-toggle">
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
        )}
        {open && <button type="button" onClick={load} className="text-slate-400 hover:text-slate-600" title="Refresh" data-testid="job-sms-refresh"><RefreshCw className="w-4 h-4" /></button>}
        <button type="button" onClick={() => setOpen((o) => !o)} className="text-slate-400 hover:text-slate-600" aria-label="Toggle SMS panel">
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} />
        </button>
      </div>

      {open && (
        <div className="px-5 py-5">
          {!customerId ? (
            <div className="text-center text-sm text-slate-400 py-6" data-testid="job-sms-no-customer">
              Link a customer to this job to send SMS messages.
            </div>
          ) : loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-400 py-6 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading conversation…</div>
          ) : (
            <>
              <div ref={scrollRef} className="max-h-96 overflow-y-auto space-y-3 mb-4 pr-1" data-testid="job-sms-messages">
                {messages.length === 0 && <div className="text-center text-sm text-slate-400 py-6">No messages yet. Start the conversation below.</div>}
                {messages.map((m) => {
                  const out = m.direction === "outbound";
                  return (
                    <div key={m.id} className={`flex ${out ? "justify-end" : "justify-start"}`} data-testid={`job-sms-msg-${m.id}`}>
                      <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 border ${out ? "bg-indigo-50 border-indigo-100" : "bg-slate-50 border-slate-200"}`}>
                        <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">{m.body}</div>
                        <div className="flex items-center gap-2 mt-1.5 text-[11px] text-slate-400">
                          <span>{out ? `To ${m.to_number || "—"}` : `From ${m.from_number || "—"}`}</span>
                          <span>·</span>
                          <span>{fmt(m.created_at)}</span>
                          {custMode && (m.job_id
                            ? <a href={`/tasks/${m.job_id}`} className="text-indigo-500 hover:underline" data-testid={`job-sms-job-link-${m.id}`}>Job {m.job_id}</a>
                            : <span>General</span>)}
                          <span className="ml-1"><StatusChip m={m} /></span>
                        </div>
                        {out && (m.status === "failed" || m.status === "undelivered") && (
                          <div className="mt-1.5 flex items-center gap-2">
                            <span className="inline-flex items-center gap-1 text-[11px] text-rose-600" data-testid={`job-sms-fail-${m.id}`}>
                              <AlertTriangle className="w-3 h-3" />{m.error || "Delivery failed"}
                            </span>
                            {maySend && (
                              <button onClick={() => retry(m)} disabled={retrying === m.id} className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-800" data-testid={`job-sms-retry-${m.id}`}>
                                {retrying === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Retry
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {maySend ? (
                <div className="rounded-xl border border-slate-200 p-3 space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <label className="text-sm font-medium text-slate-600 shrink-0">Recipient</label>
                    <Select value={recipient} onValueChange={setRecipient} disabled={numbers.length === 0}>
                      <SelectTrigger className="w-full sm:w-80" data-testid="job-sms-recipient">
                        <SelectValue placeholder={numbers.length === 0 ? "No numbers on file" : "Select a number"} />
                      </SelectTrigger>
                      <SelectContent>
                        {numbers.map((n) => (
                          <SelectItem key={n.number} value={n.number} data-testid={`job-sms-recipient-${n.number}`}>
                            {n.label}: {n.number}{n.contact_name ? ` (${n.contact_name})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {custMode && jobs.length > 0 && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <label className="text-sm font-medium text-slate-600 shrink-0">About</label>
                      <Select value={sendJob} onValueChange={setSendJob}>
                        <SelectTrigger className="w-full sm:w-80" data-testid="job-sms-about"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general" data-testid="job-sms-about-general">General (no job)</SelectItem>
                          {jobs.map((j) => <SelectItem key={j.legacy_id} value={j.legacy_id} data-testid={`job-sms-about-${j.legacy_id}`}>Job {j.legacy_id}: {j.title}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <QuickTemplateChips customerName={customerName} jobId={custMode ? (sendJob !== "general" ? sendJob : null) : legacyId} refreshKey={tplKey} onPick={setBody} />
                  <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Type an SMS to the customer…" data-testid="job-sms-input"
                    className="w-full px-3 py-2 text-sm rounded-lg bg-slate-50 border border-slate-200 text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 resize-y" />
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <SmsTemplatePicker customerName={customerName} jobId={custMode ? (sendJob !== "general" ? sendJob : null) : legacyId} onChanged={bumpTpl} onPick={(t) => setBody((b) => (b.trim() ? `${b.trimEnd()} ${t}` : t))} />
                      <span className="text-[11px] text-slate-400 truncate">{customerName ? `Customer: ${customerName}` : ""}</span>
                    </div>
                    <Button size="sm" className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy || !recipient || !body.trim()} onClick={send} data-testid="job-sms-send">
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send SMS
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-400 text-center py-2">You have read-only access to this conversation.</div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
